package com.nonopn.skydome.common.app.home

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material.ButtonDefaults
import androidx.compose.material.OutlinedButton
import androidx.compose.material.Scaffold
import androidx.compose.material.ScaffoldState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.dokar.sonner.TextToastAction
import com.dokar.sonner.Toaster
import com.dokar.sonner.rememberToasterState
import com.nonopn.skydome.common.app.checkout.CheckoutReturnBanner
import com.nonopn.skydome.common.app.drawer.DrawerSizeShape
import com.nonopn.skydome.common.app.navigation.PossibleRoutes
import com.nonopn.skydome.common.app.navigation.RouteMap
import com.nonopn.skydome.common.app.navigation.scaffold.FloatingActionButtonWrapper
import com.nonopn.skydome.common.app.navigation.scaffold.TopBarWrapper
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.app_login_title
import com.nonopn.skywatcher.common.app.res.app_main_title
import com.nonopn.skywatcher.common.login.SessionScreen
import com.nonopn.skywatcher.design.init.InitializeScreen
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.widgets.BottomSpacer
import com.nonopn.skywatcher.design.widgets.rememberSizeAwareScaffoldState
import eu.codlab.compose.widgets.StatusBarAndNavigation
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.navigation.LocalNavigator
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.NavigatorLocalProvider
import eu.codlab.navigation.ScaffoldContentWrapper
import eu.codlab.safearea.views.SafeArea
import eu.codlab.safearea.views.SafeAreaBehavior

val LocalMenuState: ProvidableCompositionLocal<ScaffoldState?> =
    compositionLocalOf { null }

@Composable
@Suppress("LongMethod", "ComplexMethod")
fun AppContent() {
    StatusBarAndNavigation()

    val scaffoldState = rememberSizeAwareScaffoldState()
    val scope = rememberCoroutineScope()
    val model = LocalApp.current
    val toaster = rememberToasterState()
    model.toaster = toaster
    val currentState by model.states.collectAsState()

    BottomSpacer {
        Toaster(
            state = toaster,
            containerPadding = PaddingValues(
                vertical = it + 16.dp
            ),
            actionSlot = { toast ->
                val action = toast.action
                if (action !is TextToastAction) return@Toaster

                OutlinedButton(
                    onClick = { action.onClick(toast) },
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
                    colors = ButtonDefaults.buttonColors(backgroundColor = AppColor.Black)
                ) {
                    TextNormal(
                        action.text,
                        color = AppColor.WhiteCream
                    )
                }
            }
        )
    }

    LaunchedEffect(scaffoldState) {
        scaffoldState.drawerState.close()
    }

    LaunchedEffect(model, scope, scaffoldState) {
        model.mainCompositionScope = scope
        model.scaffoldState = scaffoldState
    }

    if (!currentState.initialized) {
        // The payment confirmation is shown from the URL alone, before the session is ready
        Column(Modifier.fillMaxSize()) {
            CheckoutReturnArea(model)

            InitializeScreen(
                LocalApp.current,
                Modifier.fillMaxSize()
            )
        }

        return
    }

    val state by model.states.collectAsState()

    if (null == state.apiProvider) {
        Column(Modifier.fillMaxSize()) {
            CheckoutReturnArea(model)

            SessionScreen(
                Modifier.fillMaxSize(),
                appMainTitle = Res.string.app_main_title,
                loginTitle = Res.string.app_login_title,
                model
            )
        }
        return
    }

    NavigatorLocalProvider(model) {
        model.navigator = LocalNavigator.current
        val currentRoute = state.currentRoute

        val onMenuItemSelected: (String, NavigateTo) -> Unit = { newTitle, path ->
            model.show(path)
        }

        val drawerContent: @Composable (
            Modifier,
            (String, NavigateTo) -> Unit,
            Boolean
        ) -> Unit = { modifier, onMenuItemSelected, tiny ->
            DrawerContentNormal(
                modifier = modifier,
                currentRoute = currentRoute,
                onMenuItemSelected = onMenuItemSelected,
                tiny = tiny
            )
        }

        CompositionLocalProvider(LocalMenuState provides scaffoldState) {
            SafeArea(
                SafeAreaBehavior(
                    extendToTop = true,
                    extendToBottom = true,
                    extendToStart = true,
                    extendToEnd = true
                )
            ) {
                Column {
                    CheckoutReturnArea(model)

                    Scaffold(
                        scaffoldState = scaffoldState,
                        topBar = { TopBarWrapper(disableMenuInBigScreen = true) },
                        floatingActionButton = { FloatingActionButtonWrapper() },
                        // drawerGesturesEnabled = !LocalWindow.current.isScreenExpanded(),
                        drawerContent = {
                            drawerContent(
                                Modifier.fillMaxSize(),
                                onMenuItemSelected,
                                false
                            )
                        },
                        drawerShape = DrawerSizeShape(),
                        content = {
                            ScaffoldContentWrapper(
                                possibleRoutes = PossibleRoutes.entries,
                                defaultRoute = state.defaultRoute ?: RouteMap,
                                onMenuItemSelected = onMenuItemSelected
                            ) { modifier, onMenuItemSelected, tiny ->
                                // drawerContent(modifier, onMenuItemSelected, tiny)
                            }
                        }
                    )
                }
            }
        }
    }
}

/** Banner of the return from the Stripe Checkout, above the current screen (nothing when there is none). */
@Composable
private fun CheckoutReturnArea(model: AppModel) {
    val state by model.states.collectAsState()
    val phase = state.checkoutReturn ?: return

    CheckoutReturnBanner(
        phase = phase,
        onDismiss = { model.dismissCheckoutReturn() }
    )
}
